import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  HrFlexContainer,
  HrTitleContainer,
  HrTitleH4,
  HrButtons,
  HrInputText,
  HrLabel
} from '@components/styles/hr-styles';
import { HrSelect } from '@components/form/select';
import { Modal } from '../../../postulation_form/components/modal';

const Button = styled(HrButtons)`
  font-size: 16px;
  padding: 0 12px;
`;

export const SendRequestModal = ({
  templates,
  setShowModalSendRequest,
  onShowToast,
  onOpenSendLink,
  onRefreshTemplates
}) => {
  const [t] = useTranslation();
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const sortedTemplates = useMemo(() => {
    if (!Array.isArray(templates)) return [];

    return [...templates].sort((currentTemplate, nextTemplate) => {
      const currentIsDefault = currentTemplate?.isDefault === true;
      const nextIsDefault = nextTemplate?.isDefault === true;

      if (currentIsDefault && !nextIsDefault) return -1;
      if (!currentIsDefault && nextIsDefault) return 1;

      const currentName = `${currentTemplate?.templateName || ''}`.trim();
      const nextName = `${nextTemplate?.templateName || ''}`.trim();

      return currentName.localeCompare(nextName, 'es', { sensitivity: 'base' });
    });
  }, [templates]);

  useEffect(() => {
    if (!selectedTemplateId && sortedTemplates.length) {
      const defaultTemplate = sortedTemplates.find((item) => item?.isDefault === true) || sortedTemplates[0];
      setSelectedTemplateId(defaultTemplate?._id || '');
    }
  }, [sortedTemplates, selectedTemplateId]);

  const selectedTemplate = useMemo(
    () => sortedTemplates.find((item) => item?._id === selectedTemplateId) || null,
    [sortedTemplates, selectedTemplateId]
  );

  const templateOptions = useMemo(
    () => sortedTemplates.map((item) => ({ value: item._id, label: item.templateName })),
    [sortedTemplates]
  );

  const requestLink = useMemo(() => {
    if (!selectedTemplate?.hash) return '';
    return `${window.location.origin}/app/vacancy-request-form-links/${selectedTemplate.hash}/vacancy-requests/new`;
  }, [selectedTemplate]);

  const handleCopy = () => {
    if (!requestLink) return;
    navigator.clipboard.writeText(requestLink);
    onShowToast({ visible: true, message: t('requestVacancies.colaborativeSelection.modal.sendRequest.clipboard') });
  };

  const handleModalSendLink = () => {
    if (!requestLink) return;
    onOpenSendLink(requestLink);
  };

  const isDisabledActions = !requestLink;

  return (
    <Modal setShowModal={setShowModalSendRequest}>
      <HrTitleContainer padding='32px 32px 12px 32px'>
        <i className='hr-Link' />
        <HrTitleH4>{t('requestVacancies.colaborativeSelection.modal.sendRequest.title')}</HrTitleH4>
      </HrTitleContainer>
      <HrFlexContainer padding='16px 32px 0 32px' flexDirection='column' gap='8px' alignItems='flex-start' width='100%'>
        <HrLabel htmlFor='templateType' userSelect='none'>
          Tipo de solicitud
        </HrLabel>
        <HrSelect
          inputId='templateType'
          data={templateOptions}
          selected={selectedTemplateId}
          isSearchable
          onChange={(value) => setSelectedTemplateId(value)}
        />

        <HrLabel htmlFor='link' userSelect='none'>
          Enlace de la solicitud
        </HrLabel>
        <HrFlexContainer flexWrap='nowrap' gap='8px' width='100%'>
          <HrInputText
            id='link'
            name='link'
            type='text'
            disabled
            value={requestLink}
            placeholder={t('requestVacancies.colaborativeSelection.modal.sendRequest.placeholder')}
          />
          <Button
            category='tertiaryGray'
            size='medium'
            icon
            onClick={handleCopy}
            title='Copiar link'
            disabled={isDisabledActions}
          >
            <i className='hr-Copy' />
          </Button>
          <Button
            category='tertiaryGray'
            size='medium'
            icon
            onClick={handleModalSendLink}
            title='Enviar por email'
            disabled={isDisabledActions}
          >
            <i className='hr-email' />
          </Button>
          <Button category='tertiaryGray' size='medium' icon onClick={onRefreshTemplates} title='Actualizar plantillas'>
            <i className='hr-Story' />
          </Button>
        </HrFlexContainer>
      </HrFlexContainer>
      <HrFlexContainer padding='32px'>
        <HrButtons
          type='button'
          category='cancel'
          size='medium'
          onClick={() => {
            setShowModalSendRequest(false);
          }}
        >
          {t('general.button.close')}
        </HrButtons>
      </HrFlexContainer>
    </Modal>
  );
};
